import React, { useState } from 'react';
import { Input } from './input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

interface CountryCode {
  code: string;
  flag: string;
  name: string;
  format: (phone: string) => string;
  maxLength: number;
  hint?: string;
}

const countryCodes: { [key: string]: CountryCode } = {
  '+54': {
    code: '+54',
    flag: '🇦🇷',
    name: 'Argentina',
    // El usuario escribe el número completo (incluido el 9 de celular si corresponde).
    // No se agrupa: se muestran los dígitos tal cual para que quede claro.
    format: (phone: string) => phone.replace(/\D/g, '').slice(0, 11),
    maxLength: 11, // 9 (celular) + 10 dígitos del número nacional
    hint: 'Para celulares, incluí el 9 antes del código de área. Ej: 9 297 5280428',
  },
  '+1': {
    code: '+1',
    flag: '🇺🇸',
    name: 'United States',
    format: (phone: string) => {
      // Formato: +1 555 123 4567
      const numbers = phone.replace(/\D/g, '');
      if (numbers.length <= 3) {
        return numbers;
      } else if (numbers.length <= 6) {
        return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
      }
      return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 10)}`;
    },
    maxLength: 10,
  },
  '+44': {
    code: '+44',
    flag: '🇬🇧',
    name: 'United Kingdom',
    format: (phone: string) => {
      // Formato: +44 20 1234 5678
      const numbers = phone.replace(/\D/g, '');
      if (numbers.length <= 2) {
        return numbers;
      } else if (numbers.length <= 6) {
        return `${numbers.slice(0, 2)} ${numbers.slice(2)}`;
      }
      return `${numbers.slice(0, 2)} ${numbers.slice(2, 6)} ${numbers.slice(6, 10)}`;
    },
    maxLength: 10,
  },
  '+55': {
    code: '+55',
    flag: '🇧🇷',
    name: 'Brasil',
    format: (phone: string) => {
      // Formato: +55 11 9 1234-5678
      const numbers = phone.replace(/\D/g, '');
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 7) return `${numbers.slice(0, 2)} ${numbers.slice(2)}`;
      return `${numbers.slice(0, 2)} ${numbers.slice(2, 3)} ${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    },
    maxLength: 11,
  },
  '+56': {
    code: '+56',
    flag: '🇨🇱',
    name: 'Chile',
    format: (phone: string) => {
      // Formato: +56 9 1234 5678
      const numbers = phone.replace(/\D/g, '');
      if (numbers.length <= 1) return numbers;
      if (numbers.length <= 5) return `${numbers.slice(0, 1)} ${numbers.slice(1)}`;
      return `${numbers.slice(0, 1)} ${numbers.slice(1, 5)} ${numbers.slice(5, 9)}`;
    },
    maxLength: 9,
  },
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  placeholder = '9 3564 335598',
  className = '',
}) => {
  const [countryCode, setCountryCode] = useState<string>('+54');

  // Extraer código de país y número del valor
  const extractPhoneData = (fullPhone: string): { code: string; number: string } => {
    const codes = Object.keys(countryCodes);
    for (const code of codes) {
      if (fullPhone.startsWith(code)) {
        return {
          code,
          number: fullPhone.slice(code.length),
        };
      }
    }
    return { code: '+54', number: fullPhone };
  };

  const { code: initialCode, number: phoneNumber } = extractPhoneData(value);

  // Si el código inicial es diferente al estado, actualizarlo
  React.useEffect(() => {
    if (initialCode !== countryCode && countryCodes[initialCode]) {
      setCountryCode(initialCode);
    }
  }, [initialCode, countryCode]);

  const currentCountry = countryCodes[countryCode];

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numbers = e.target.value.replace(/\D/g, '');
    const limited = numbers.slice(0, currentCountry.maxLength);
    onChange(limited === '' ? '' : `${countryCode}${limited}`);
  };

  const handleCountryChange = (newCode: string) => {
    setCountryCode(newCode);
    const numbers = phoneNumber.replace(/\D/g, '');
    onChange(numbers === '' ? '' : `${newCode}${numbers}`);
  };

  // Formatear para mostrar
  const displayValue = currentCountry.format(phoneNumber);

  return (
    <div className={className}>
      <div className="flex items-stretch">
        <Select value={countryCode} onValueChange={handleCountryChange}>
          <SelectTrigger className="w-[85px] sm:w-[90px] rounded-r-none border-r-0 !px-2 !py-2.5 sm:!py-3 h-auto">
            <SelectValue>
              <span className="flex items-center gap-1">
                <span className="text-base leading-none">{currentCountry.flag}</span>
                <span className="text-xs leading-none">{currentCountry.code}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(countryCodes).map(([code, country]) => (
              <SelectItem key={code} value={code}>
                <span className="flex items-center gap-2">
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm">{country.name} ({country.code})</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="tel"
          value={displayValue}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className="rounded-l-none flex-1 border-l-0"
        />
      </div>

      {currentCountry.hint && (
        <p className="mt-1 text-xs text-muted-foreground">{currentCountry.hint}</p>
      )}
    </div>
  );
};

export default PhoneInput;
